import React, { useEffect, useState } from "react";
import axios from "axios";

const API = "https://estatebackend.pixelmindsolutions.com/api/admin/banner";

/* ─── Styles ─── */
const StyleTag = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=DM+Sans:wght@300;400;500&display=swap');

    :root {
      --red-from:   #ef4444;
      --red-to:     #dc2626;
      --red-soft:   rgba(239,68,68,0.07);
      --red-border: rgba(239,68,68,0.18);
      --bg:         #f8f7f5;
      --surface:    #ffffff;
      --border:     #ede9e3;
      --ink:        #1a1410;
      --muted:      #8a8278;
      --shadow-sm:  0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
      --shadow-md:  0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04);
      --shadow-lg:  0 16px 48px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.06);
    }

    .banners-root {
      min-height: 100vh;
      background: var(--bg);
      font-family: 'DM Sans', sans-serif;
      color: var(--ink);
      padding: 48px 32px 80px;
    }

    /* ── Header ── */
    .banners-header {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      margin-bottom: 48px;
    }
    .banners-eyebrow {
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 8px;
    }
    .banners-title {
      font-family: 'Playfair Display', serif;
      font-size: clamp(2rem, 4vw, 2.8rem);
      font-weight: 600;
      line-height: 1;
      color: var(--ink);
    }
    .banners-live-badge {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 100px;
      background: var(--red-soft);
      border: 1px solid var(--red-border);
      font-size: 12px;
      font-weight: 500;
      color: var(--red-to);
      letter-spacing: 0.04em;
    }
    .live-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      animation: livepulse 2s ease infinite;
    }
    @keyframes livepulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
      50%      { box-shadow: 0 0 0 5px rgba(239,68,68,0); }
    }

    /* ── Upload Panel ── */
    .upload-panel {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 20px;
      padding: 32px;
      margin-bottom: 40px;
      box-shadow: var(--shadow-sm);
      position: relative;
      overflow: hidden;
    }
    .upload-panel::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: linear-gradient(90deg, var(--red-from), var(--red-to));
      border-radius: 20px 20px 0 0;
    }
    .upload-label {
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--muted);
      margin-bottom: 20px;
    }

    /* Edit pill */
    .edit-pill {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 5px 12px;
      border-radius: 100px;
      background: var(--red-soft);
      border: 1px solid var(--red-border);
      color: var(--red-to);
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 18px;
    }
    .edit-pill-dot {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: var(--red-from);
      animation: livepulse 1.5s ease infinite;
    }

    /* Drop zone */
    .drop-zone {
      position: relative;
      flex: 1;
      min-width: 200px;
      border: 1.5px dashed var(--red-border);
      border-radius: 14px;
      padding: 28px 20px;
      text-align: center;
      cursor: pointer;
      background: var(--red-soft);
      transition: border-color 0.2s, background 0.2s;
    }
    .drop-zone:hover, .drop-zone.over {
      border-color: var(--red-from);
      background: rgba(239,68,68,0.11);
    }
    .drop-zone input[type="file"] {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
      width: 100%; height: 100%;
    }
    .drop-icon {
      width: 40px; height: 40px;
      margin: 0 auto 10px;
      border-radius: 11px;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      display: flex; align-items: center; justify-content: center;
      color: #fff;
      font-size: 18px;
      box-shadow: 0 4px 14px rgba(239,68,68,0.3);
    }
    .drop-text {
      font-size: 13px;
      font-weight: 500;
      color: var(--ink);
    }
    .drop-sub {
      font-size: 11px;
      color: var(--muted);
      margin-top: 4px;
      letter-spacing: 0.04em;
    }

    .upload-row {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }
    .upload-preview {
      width: 130px;
      height: 84px;
      border-radius: 12px;
      object-fit: cover;
      border: 1px solid var(--border);
      box-shadow: var(--shadow-md);
      flex-shrink: 0;
      transition: transform 0.25s;
    }
    .upload-preview:hover { transform: scale(1.04); }

    .btn-actions { display: flex; flex-direction: column; gap: 8px; }

    .btn-submit {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 13px 28px;
      border-radius: 12px;
      border: none;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.04em;
      cursor: pointer;
      transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
      box-shadow: 0 4px 16px rgba(239,68,68,0.35);
      white-space: nowrap;
    }
    .btn-submit:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 8px 24px rgba(239,68,68,0.45);
    }
    .btn-submit:active:not(:disabled) { transform: translateY(0); }
    .btn-submit:disabled { opacity: 0.55; cursor: not-allowed; }

    .btn-cancel {
      padding: 10px 20px;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: transparent;
      color: var(--muted);
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: border-color 0.2s, color 0.2s, background 0.2s;
    }
    .btn-cancel:hover { border-color: #ccc; color: var(--ink); background: #f3f0ec; }

    /* ── Section row ── */
    .section-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
    }
    .section-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.3rem;
      font-weight: 600;
      color: var(--ink);
    }
    .section-count {
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--muted);
    }

    /* ── Grid ── */
    .banners-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
      gap: 20px;
    }

    /* ── Card ── */
    .banner-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      overflow: hidden;
      box-shadow: var(--shadow-sm);
      transition: transform 0.3s cubic-bezier(.22,.68,0,1.1), box-shadow 0.3s;
      animation: fadeUp 0.45s ease both;
    }
    .banner-card:hover {
      transform: translateY(-5px);
      box-shadow: var(--shadow-lg);
    }
    @keyframes fadeUp {
      from { opacity:0; transform: translateY(18px); }
      to   { opacity:1; transform: translateY(0); }
    }

    .banner-img-wrap {
      position: relative;
      height: 175px;
      overflow: hidden;
      background: #f0ece6;
    }
    .banner-img {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.5s ease;
    }
    .banner-card:hover .banner-img { transform: scale(1.05); }

    .banner-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(220,38,38,0.80) 0%, rgba(239,68,68,0.1) 55%, transparent 100%);
      opacity: 0;
      transition: opacity 0.3s;
      display: flex;
      align-items: flex-end;
      padding: 14px;
    }
    .banner-card:hover .banner-overlay { opacity: 1; }

    .btn-del-overlay {
      flex: 1;
      padding: 10px;
      border-radius: 9px;
      border: 1px solid rgba(255,255,255,0.35);
      background: rgba(255,255,255,0.15);
      backdrop-filter: blur(6px);
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: background 0.2s;
    }
    .btn-del-overlay:hover { background: rgba(255,255,255,0.25); }

    .banner-footer {
      padding: 12px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-top: 1px solid var(--border);
    }
    .banner-id {
      font-size: 10px;
      letter-spacing: 0.07em;
      color: #c0b9b1;
    }
    .banner-status {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 11px;
      font-weight: 500;
      color: var(--red-to);
    }
    .status-dot {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--red-from), var(--red-to));
    }

    /* ── Empty ── */
    .empty-state {
      grid-column: 1 / -1;
      text-align: center;
      padding: 80px 20px;
    }
    .empty-icon-wrap {
      width: 72px; height: 72px;
      border-radius: 20px;
      background: var(--red-soft);
      border: 1px solid var(--red-border);
      display: flex; align-items: center; justify-content: center;
      font-size: 28px;
      margin: 0 auto 20px;
    }
    .empty-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.4rem;
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 6px;
    }
    .empty-sub {
      font-size: 13px;
      color: var(--muted);
    }

    /* ── Spinner ── */
    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner {
      width: 14px; height: 14px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }

    /* ── Toast ── */
    .toast {
      position: fixed;
      bottom: 28px; right: 28px;
      background: var(--surface);
      border: 1px solid var(--border);
      border-left: 3px solid var(--red-from);
      border-radius: 12px;
      padding: 13px 18px;
      font-size: 13px;
      font-weight: 500;
      color: var(--ink);
      box-shadow: var(--shadow-lg);
      animation: toastIn 0.3s ease;
      z-index: 999;
    }
    @keyframes toastIn {
      from { opacity:0; transform: translateY(12px); }
      to   { opacity:1; transform: translateY(0); }
    }
  `}</style>
);

/* ─── Toast hook ─── */
const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };
  return { toast, show };
};

/* ═══════════════════════════════════════════════════════ */

const Banners = () => {
  const [banners, setBanners]   = useState([]);
  const [image, setImage]       = useState(null);
  const [preview, setPreview]   = useState(null);
  const [editId, setEditId]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [dragover, setDragover] = useState(false);
  const { toast, show }         = useToast();

  const fetchBanners = async () => {
    try {
      const res = await axios.get(API);
      setBanners(res.data.banners || []);
    } catch {
      show("Failed to load banners");
    }
  };
  useEffect(() => { fetchBanners(); }, []);

  const handleImage = (file) => {
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!image) { show("Please select an image"); return; }
    const fd = new FormData();
    fd.append("banner", image);
    if (editId) fd.append("bannerIds", JSON.stringify([editId]));
    try {
      setLoading(true);
      if (editId) {
        await axios.put(API, fd);
        show("Banner updated successfully");
      } else {
        await axios.post(API, fd);
        show("Banner published successfully");
      }
      resetForm();
      fetchBanners();
    } catch {
      show("Upload failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this banner?")) return;
    try {
      await axios.delete(API, { data: { bannerIds: [id] } });
      setBanners((prev) => prev.filter((b) => b._id !== id));
      show("Banner removed");
    } catch {
      show("Delete failed");
    }
  };

  const resetForm = () => { setImage(null); setPreview(null); setEditId(null); };

  return (
    <>
      <StyleTag />
      <div className="banners-root">

        {/* HEADER */}
        <div className="banners-header">
          <div>
            <div className="banners-eyebrow">Admin · Media</div>
            <h1 className="banners-title">Banner Gallery</h1>
          </div>
          <div className="banners-live-badge">
            <span className="live-dot" />
            {banners.length} Live
          </div>
        </div>

        {/* UPLOAD PANEL */}
        <div className="upload-panel">
          <div className="upload-label">
            {editId ? "Edit Banner" : "Upload New Banner"}
          </div>

          {editId && (
            <div className="edit-pill">
              <span className="edit-pill-dot" />
              Editing · {editId.slice(-6).toUpperCase()}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="upload-row">
              <div
                className={`drop-zone${dragover ? " over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
                onDragLeave={() => setDragover(false)}
                onDrop={(e) => {
                  e.preventDefault(); setDragover(false);
                  const f = e.dataTransfer.files[0];
                  if (f?.type.startsWith("image/")) handleImage(f);
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImage(e.target.files[0])}
                />
                <div className="drop-icon">↑</div>
                <div className="drop-text">
                  {image ? image.name : "Drop image or click to browse"}
                </div>
                <div className="drop-sub">PNG · JPG · WEBP — max 10 MB</div>
              </div>

              {preview && (
                <img src={preview} alt="preview" className="upload-preview" />
              )}

              <div className="btn-actions">
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? <span className="spinner" /> : null}
                  {loading ? "Uploading…" : editId ? "Update Banner" : "Publish Banner"}
                </button>
                {(image || editId) && (
                  <button type="button" className="btn-cancel" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* SECTION */}
        <div className="section-row">
          <div className="section-title">All Banners</div>
          <div className="section-count">{banners.length} total</div>
        </div>

        {/* GRID */}
        <div className="banners-grid">
          {banners.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon-wrap">🖼</div>
              <div className="empty-title">No banners yet</div>
              <div className="empty-sub">Upload your first banner using the panel above</div>
            </div>
          ) : (
            banners.map((banner, idx) => (
              <div
                className="banner-card"
                key={banner._id}
                style={{ animationDelay: `${idx * 55}ms` }}
              >
                <div className="banner-img-wrap">
                  <img src={banner.image} alt="banner" className="banner-img" />
                  <div className="banner-overlay">
                    <button
                      className="btn-del-overlay"
                      onClick={() => handleDelete(banner._id)}
                    >
                      Remove Banner
                    </button>
                  </div>
                </div>
                <div className="banner-footer">
                  <span className="banner-id">#{banner._id.slice(-8).toUpperCase()}</span>
                  <span className="banner-status">
                    <span className="status-dot" /> Active
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
      {toast && <div className="toast">{toast}</div>}
    </>
  );
};

export default Banners;